import '@fpsm/cxy-tokens/tokens.css';
import './oc-variables.css';

import { useMemo } from 'react';
import clsx from 'clsx';

import { categoryGroups, REPUBS_WITH_BOOKIE_LOGOS } from '@cx-modules/constants';
import { type Namespace, useTranslation } from '@cx-modules/i18n-provider';
import { type OddsFormat } from '@cx-modules/types';
import { OddsButton } from '@cx-modules/ui';
import { roundOdds } from '@cx-modules/utils';

import { type PopularBet } from '../interfaces';

import { useMostPopularBetsProps } from './context/MostPopularBetsContext';
import { useAnalytics } from './hooks/useAnalytics';
import { useBetslip } from './hooks/useBetslip';
import { ALL_SPORTS_VALUE, useMostPopularBetsAPI } from './hooks/useMostPopularBetsAPI';
import { useTrackImpression } from './hooks/useTrackImpression';
import { getSportName } from './utils/getSportName';
import { getTranslatedBetName } from './utils/getTranslatedBetName';
import { getTranslatedSubeventName } from './utils/getTranslatedSubeventName';
import styles from './MostPopularBets.module.scss';
import { SportFilter } from './SportFilter';
import { SportIcon } from './SportIcon';

const getOddsText = (oddsFormat: OddsFormat, bet: PopularBet) => {
  switch (oddsFormat) {
    case 'american':
      return bet.bestOddsUs ?? '';
    case 'fractional':
      return bet.bestOddsFractional ?? '';
    case 'decimal':
      return bet.bestOddsDecimal != null ? roundOdds(bet.bestOddsDecimal) : '';
    default:
      return '';
  }
};

const PopularBetRow = ({
  bet,
  oddsFormat,
  position,
  renderBadge,
  selectedSport,
  showSportIcon,
}: {
  bet: PopularBet;
  oddsFormat: OddsFormat;
  position: number;
  renderBadge: boolean;
  selectedSport: string;
  showSportIcon: boolean;
}) => {
  const { bookmakersConfig, onOddsButtonClick } = useMostPopularBetsProps();
  const { track } = useAnalytics();

  const bookieCode = bet.bestOddsBookmakerCode ?? '';
  const bookmakerConfig = bookmakersConfig?.[bookieCode];
  const oddsText = getOddsText(oddsFormat, bet);

  // Analytics payloads use the raw (canonical, untranslated) bet/subevent names and sport.
  const analyticsBase = {
    betId: bet.id,
    betName: bet.name,
    position,
    selectedSport,
    sport: bet.categoryGroupName,
    subeventName: bet.subeventName,
  };

  // Always attempt to translate the bet and subevent names against the sport/label/bettype
  // namespaces and fall back to the raw text — so a new locale just needs translation files,
  // with no repub allow-list to maintain. The subevent name follows the Subevent Header
  // mechanism (split team matchups, translate each side); the bet name follows the odds-grid
  // mechanism (name used as a key, line appended untranslated).
  const categoryGroup = categoryGroups.find(cg => cg.id === bet.categoryGroupId);
  const categoryNs = categoryGroup?.translationFile || '';
  const { t } = useTranslation([categoryNs, 'label', 'bettype'] as Namespace[]);
  const subeventName = getTranslatedSubeventName(bet.subeventName, t);
  const betName = getTranslatedBetName(bet.name, bet.line, t);

  const { isBookieBetSelected, toggleBetslipBet } = useBetslip({
    betId: bet.id,
    betName: bet.name,
    betOdds: oddsText,
    bookieCode,
    marketName: bet.marketName,
    subeventName: bet.subeventName,
  });

  const handleOddsClick = () => {
    track('most_popular_bets_odds_click', {
      ...analyticsBase,
      bookmakerCode: bookieCode,
      marketName: bet.marketName,
      oddsText,
    });
    if (onOddsButtonClick) {
      return onOddsButtonClick('most_popular_bets_click', bet.id, bookieCode, bet.name, oddsText);
    }
    toggleBetslipBet();
  };

  const handleSelectionClick = () => {
    track('most_popular_bets_selection_click', { ...analyticsBase, subeventUrl: bet.subeventUrl });
  };

  return (
    <li className={styles.row} data-testid="most-popular-bets-row" key={bet.id}>
      <div className={clsx(styles.link, !showSportIcon && styles.linkNoIcon)}>
        {showSportIcon && <SportIcon categoryGroupId={bet.categoryGroupId} />}
        <a className={styles.betInfo} href={bet.subeventUrl ?? '#'} onClick={handleSelectionClick}>
          <span className={styles.betName}>{betName}</span>
          <span className={styles.subeventName}>{subeventName}</span>
        </a>
        {oddsText && bookieCode ? (
          <OddsButton
            bookieCode={bookieCode}
            bookmakerConfig={bookmakerConfig}
            className={clsx(styles.oddsButton, isBookieBetSelected && styles.selected)}
            isSelected={isBookieBetSelected}
            onClick={handleOddsClick}
            renderBadge={renderBadge}
          >
            {oddsText}
          </OddsButton>
        ) : (
          <span className={styles.odds}>{oddsText}</span>
        )}
      </div>
    </li>
  );
};

export const MostPopularBets = () => {
  const { className, emptySlot, errorSlot, oddsFormat = 'decimal', repub, showBookieLogos } = useMostPopularBetsProps();
  const { t } = useTranslation('label');

  // The heading always comes from the translation label, never the CMS. Falls back to English
  // when the host has no translation for the key.
  const resolvedTitle = t('CX_MOST_POPULAR_BETS', { defaultValue: 'Most Popular Bets' });

  const renderBadge = showBookieLogos ?? (repub != null && REPUBS_WITH_BOOKIE_LOGOS.includes(repub));
  const {
    allSportsBets,
    bets,
    error,
    includeAll,
    isSingleTarget,
    selectedCategoryGroupId,
    setSelectedCategoryGroupId,
    sports,
  } = useMostPopularBetsAPI();
  const { analyticsDataLayer, analyticsImpressionEventData, impressionEventName } = useAnalytics();

  // A subevent/event target shows a single pool, so the All-Sports view does not apply.
  const isAllSports = selectedCategoryGroupId === ALL_SPORTS_VALUE;
  const visibleBets = !isSingleTarget && isAllSports && includeAll ? allSportsBets : bets;

  // Show the sport icon whenever the sport dropdown is present, so switching sports keeps the
  // row layout stable rather than shifting when icons appear/disappear. For a single
  // event/subevent target there is no dropdown and the sport is implied, so the icon is omitted.
  const showSportIcons = !isSingleTarget && sports.length > 0;

  // Canonical (untranslated) sport name for analytics: 'All Sports' or the configured label.
  const selectedSport = getSportName(sports, selectedCategoryGroupId);

  // Impression fires once the widget is in view; only active when an impression event name is set.
  // Memoise the payload so its identity is stable — otherwise a new object each render would
  // re-fire the impression on every re-render while the widget stays in view.
  const impressionData = useMemo(
    () => ({ ...analyticsImpressionEventData, betCount: visibleBets.length, selectedSport }),
    [visibleBets.length, selectedSport, analyticsImpressionEventData],
  );
  const impressionRef = useTrackImpression(impressionEventName, impressionData, analyticsDataLayer);

  if (error) return errorSlot ?? null;

  // Collapse the whole widget — heading and sport filter included — only when there is nothing
  // to show AND no other view the user could switch to, rather than leaving a bare title behind.
  // No alternative exists when: the target is a single event/subevent (no dropdown); the
  // All-Sports (includeAll) pool is shown and empty (it is the union across sports, so an empty
  // union means every sport is empty too); or there is a lone fixed sport with no real dropdown.
  // For a specific sport inside a multi-option dropdown we KEEP the header + filter even when
  // that sport is empty, so the user can switch to one that has bets.
  const noAlternativeView =
    isSingleTarget || (isAllSports && includeAll === true) || (sports.length <= 1 && !includeAll);
  if (visibleBets.length === 0 && noAlternativeView) return emptySlot ?? null;

  return (
    <div className={clsx(styles.container, className)} data-testid="most-popular-bets-container" ref={impressionRef}>
      <div className={styles.header}>
        <h2 className={styles.title}>{resolvedTitle}</h2>
        {!isSingleTarget && sports.length > 0 && (
          <SportFilter
            includeAll={includeAll ?? false}
            onSelect={setSelectedCategoryGroupId}
            selectedCategoryGroupId={selectedCategoryGroupId}
            sports={sports}
          />
        )}
      </div>

      {visibleBets.length === 0 ? (
        (emptySlot ?? null)
      ) : (
        <ul className={styles.list}>
          {visibleBets.map((bet, index) => (
            <PopularBetRow
              bet={bet}
              key={bet.id}
              oddsFormat={oddsFormat}
              position={index + 1}
              renderBadge={renderBadge}
              selectedSport={selectedSport}
              showSportIcon={showSportIcons}
            />
          ))}
        </ul>
      )}
    </div>
  );
};
